/**
 * Pure helpers for the Social Media Hub: caption + hashtag construction.
 * Extracted from SocialPostModal so the component stays focused on
 * rendering and state, and so these functions can be unit-tested easily.
 */

export const PROD_ORIGIN = 'https://romenightlife.it';

const GIORNI = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

/** Format "YYYY-MM-DD" into a long Italian date like "Sabato 14 Giugno 2026". */
export function formatItalianDateLong(dateStr) {
  if (!dateStr) return '';
  try {
    const [y, m, d] = String(dateStr).split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return dateStr;
    const dt = new Date(y, m - 1, d);
    return `${GIORNI[dt.getDay()]} ${d} ${MESI[m - 1]} ${y}`;
  } catch { return dateStr; }
}

/** Slugify a string for use as a hashtag: no accents, no whitespace, no punctuation. */
export function toHashtag(s) {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 30);
}

/**
 * Build the full Instagram caption from an event + its locale.
 * Structure: title / date+time / venue / CTA / UTM link / 5 hashtags.
 */
export function buildSocialCaption(evento, locale) {
  const titolo = (evento?.titolo || '').trim();
  const dataStr = formatItalianDateLong(evento?.data);
  const orario = (evento?.orario || '').trim();
  const venueName = (locale?.nome || '').trim();
  const zona = (evento?.zona || locale?.zona || '').trim();
  const slug = evento?.slug || evento?.id || '';

  const utmLink = `${PROD_ORIGIN}/eventi/${slug}?utm_source=instagram&utm_medium=social`;

  const hashtags = [
    'romanightlife',
    'eventiroma',
    'romabynight',
    toHashtag(zona) || 'roma',
    toHashtag(venueName) || 'nightlife',
  ].filter(Boolean).map((h) => `#${h}`).join(' ');

  const dateLine = [dataStr, orario].filter(Boolean).join(' • ');

  return [
    titolo,
    '',
    dateLine ? `📅 ${dateLine}` : '',
    venueName ? `📍 ${venueName}${zona ? ` — ${zona}` : ''}` : '',
    '',
    'Link in bio! 👉',
    '',
    utmLink,
    '',
    hashtags,
  ].join('\n');
}

/**
 * Copy a string to the clipboard, with a document.execCommand fallback for
 * older browsers or contexts where the Clipboard API is unavailable.
 */
export async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  ta.remove();
}
